import { useRef, useEffect } from 'react';
import EventForm from './EventForm';
import MilestoneForm from './MilestoneForm';
import { useCalendarDataContext } from '../../contexts/CalendarDataContext';
import { useSidebarContext } from '../../contexts/SidebarContext';
import { SIDEBAR_TYPE } from '../../constants/types';
import './DetailsSidebar.scss';

/**
 * Right sidebar panel for creating and editing events and milestones.
 * Slides in from the right edge of the screen when open.
 * Uses key prop to reset form state when switching between entries.
 * 
 * Container component that delegates to EventForm or MilestoneForm based on type.
 * 
 * @param {Object} props
 * @param {Function} props.onSave - Handler for save (formData) => void
 * @param {Function} props.onDelete - Handler for delete (id) => void
 */
function DetailsSidebar({ 
  onSave, 
  onDelete
}) {
  // Get data and state from context
  const { projects, selectedProjectId } = useCalendarDataContext();
  const {
    sidebarOpen: isOpen,
    sidebarType: type,
    sidebarMode: mode,
    sidebarData: data,
    closeSidebar: onClose,
    onSidebarChange: onChange
  } = useSidebarContext();

  const sidebarRef = useRef(null);

  // Handle escape key at container level
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  // Handle click outside to close sidebar
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (e) => {
      if (sidebarRef.current && !sidebarRef.current.contains(e.target)) {
        onClose();
      }
    };

    // Add slight delay to avoid closing immediately if opened by a click
    const timeoutId = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
    }, 100);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);

  // Generate a unique key that changes when we switch entries
  // This forces React to remount the form with fresh state
  const formKey = `${mode}-${type}-${data?.id || 'new'}-${isOpen}`;

  return (
    <div 
      ref={sidebarRef}
      className={`details-sidebar ${isOpen ? 'details-sidebar--open' : ''}`}
    >
      {isOpen && type === SIDEBAR_TYPE.EVENT && (
        <EventForm
          key={formKey}
          mode={mode}
          data={data}
          projects={projects}
          selectedProjectId={selectedProjectId}
          onSave={onSave}
          onDelete={onDelete}
          onClose={onClose}
          onChange={onChange}
        />
      )}
      {isOpen && type === SIDEBAR_TYPE.MILESTONE && (
        <MilestoneForm
          key={formKey}
          mode={mode}
          data={data}
          projects={projects}
          selectedProjectId={selectedProjectId}
          onSave={onSave}
          onDelete={onDelete}
          onClose={onClose}
          onChange={onChange}
        />
      )}
    </div>
  );
}

export default DetailsSidebar;
